import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchMidInspections } from '../../api/midInspection';
import { reportError } from '../../utils/notify';
import { CompanyInventoryItem, formatInventoryQty } from '../../types/companyInventory';
import { MidInspection, midInspectionStatusLabel } from '../../types/midInspection';
import './ProductionDailyLotPicker.css';

export interface MidProductionLotPick {
  midInspectionId: number;
  inventoryId: number;
  itemName: string;
  specText: string;
  lotNo: string;
  lotQty: string;
}

interface Props {
  inventory: CompanyInventoryItem;
  onClose: () => void;
  onSelect: (pick: MidProductionLotPick) => void;
}

function MidProductionLotPicker({ inventory, onClose, onSelect }: Props) {
  const [rows, setRows] = useState<MidInspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const inventoryId = Number(inventory.inventoryId);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!Number.isFinite(inventoryId) || inventoryId <= 0) {
        setError('모델을 먼저 선택해 주세요.');
        setRows([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError('');
        const list = await fetchMidInspections({ inventoryId });
        if (cancelled) return;
        setRows(
          list
            .filter(
              (row) =>
                Number(row.inventoryId) === inventoryId &&
                row.status === 'APPROVED' &&
                Boolean((row.lotNo || '').trim())
            )
            .sort((a, b) => (b.inspectionId || 0) - (a.inspectionId || 0))
        );
      } catch {
        if (!cancelled) {
          reportError(setError, '중간검사 생산 Lot 목록을 불러오지 못했습니다.');
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [inventoryId]);

  const stockLabel = useMemo(
    () => `${formatInventoryQty(inventory.stockQty)}${inventory.unit?.trim() || ''}`,
    [inventory.stockQty, inventory.unit]
  );

  return createPortal(
    <div className="pdr-lot-picker__backdrop" role="presentation" onClick={onClose}>
      <div
        className="pdr-lot-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mid-prod-lot-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pdr-lot-picker__head">
          <div>
            <p className="pdr-lot-picker__eyebrow">MID LOT</p>
            <h3 id="mid-prod-lot-picker-title">중간검사 생산 Lot 선택</h3>
            <p className="pdr-lot-picker__sub">
              {inventory.itemName}
              {inventory.specText?.trim() ? ` · ${inventory.specText.trim()}` : ''}
              {' · '}현재 재고 {stockLabel}
            </p>
          </div>
          <button type="button" className="pdr-lot-picker__close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>

        <div className="pdr-lot-picker__body">
          {loading ? <p className="pdr-lot-picker__hint">불러오는 중...</p> : null}
          {error ? <p className="pdr-lot-picker__error">{error}</p> : null}
          {!loading && !error && rows.length === 0 ? (
            <p className="pdr-lot-picker__hint">
              이 모델의 승인된 중간검사 생산 Lot이 없습니다. 중간검사를 먼저 승인하거나 Lot을 직접
              입력하세요.
            </p>
          ) : null}
          {!loading && rows.length > 0 ? (
            <>
              <p className="pdr-lot-picker__count">
                승인된 중간검사 {rows.length}건 · 선택 시 생산 Lot·수량이 제품검사에 반영됩니다
              </p>
              <div className="pdr-lot-picker__scroll">
                <table className="pdr-lot-picker__table">
                  <thead>
                    <tr>
                      <th>모델</th>
                      <th>생산 Lot</th>
                      <th>생산수량</th>
                      <th>검사일</th>
                      <th>판정</th>
                      <th>상태</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.inspectionId}>
                        <td>{row.itemName?.trim() || inventory.itemName || '-'}</td>
                        <td>{row.lotNo?.trim() || '-'}</td>
                        <td>{row.lotQty?.trim() || '-'}</td>
                        <td>{row.inspectionDate?.slice(0, 10) || '-'}</td>
                        <td>{row.overallJudgment?.trim() || '-'}</td>
                        <td>{midInspectionStatusLabel(row.status)}</td>
                        <td>
                          <button
                            type="button"
                            className="pdr-lot-picker__pick"
                            onClick={() =>
                              onSelect({
                                midInspectionId: row.inspectionId,
                                inventoryId: row.inventoryId || inventory.inventoryId,
                                itemName: row.itemName || inventory.itemName || '',
                                specText: row.specText || inventory.specText || '',
                                lotNo: row.lotNo?.trim() || '',
                                lotQty: row.lotQty?.trim() || '',
                              })
                            }
                          >
                            선택
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
        </div>

        <div className="pdr-lot-picker__actions">
          <button type="button" onClick={onClose}>
            직접 입력
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default MidProductionLotPicker;
